import { useEffect, useRef } from "react";
import { LOW, isSwiping } from "@/lib/perf";

// 2D ripples from finger/cursor. Window-level listeners + pointer-events:none so taps always reach buttons. 30fps, DPR 1, idle when still.
const LIFE = 1600;
const MAX = 18;
const FRAME = 1000 / 30;

export const WaterRipple = () => {
  const ref = useRef(null);
  useEffect(() => {
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return undefined;
    const cv = ref.current;
    const ctx = cv.getContext("2d");
    const rings = [];
    let raf = 0, last = 0, lastSpawn = 0, lx = -999, ly = -999;
    const size = () => { cv.width = Math.min(window.innerWidth, 1600); cv.height = Math.min(window.innerHeight, 1200); };
    const sx = () => cv.width / window.innerWidth;
    const draw = (now) => {
      raf = 0;
      if (document.hidden) return;
      if (now - last < FRAME) { raf = requestAnimationFrame(draw); return; }
      last = now;
      ctx.clearRect(0, 0, cv.width, cv.height);
      for (let i = rings.length - 1; i >= 0; i--) {
        const r = rings[i];
        const t = Math.max(0, (now - r.t0) / LIFE);
        if (t >= 1) { rings.splice(i, 1); continue; }
        const rad = r.s * (8 + t * 150), a = (1 - t) * (1 - t) * r.w;
        ctx.lineWidth = 2;
        ctx.strokeStyle = `rgba(255,255,255,${a * 0.7})`;
        ctx.beginPath(); ctx.ellipse(r.x, r.y, rad, rad * 0.62, 0, 0, Math.PI * 2); ctx.stroke();
        ctx.strokeStyle = `rgba(15,138,150,${a * 0.22})`;
        ctx.beginPath(); ctx.ellipse(r.x, r.y, rad * 0.72, rad * 0.45, 0, 0, Math.PI * 2); ctx.stroke();
      }
      if (rings.length) raf = requestAnimationFrame(draw);
    };
    const spawn = (x, y, w, s) => {
      const k = sx();
      rings.push({ x: x * k, y: y * k, t0: performance.now(), w, s: s * k });
      if (rings.length > MAX) rings.shift();
      if (!raf) raf = requestAnimationFrame(draw);
    };
    const down = (e) => { spawn(e.clientX, e.clientY, 0.9, 1.2); lx = e.clientX; ly = e.clientY; };
    const move = (e) => {
      const now = performance.now();
      if (LOW && isSwiping()) return;
      if (now - lastSpawn < 90 || Math.hypot(e.clientX - lx, e.clientY - ly) < 36) return;
      lastSpawn = now; lx = e.clientX; ly = e.clientY;
      spawn(e.clientX, e.clientY, 0.45, 0.7);
    };
    const vis = () => { if (document.hidden) { cancelAnimationFrame(raf); raf = 0; rings.length = 0; ctx.clearRect(0, 0, cv.width, cv.height); } };
    size();
    window.addEventListener("resize", size);
    window.addEventListener("pointerdown", down, { passive: true });
    window.addEventListener("pointermove", move, { passive: true });
    document.addEventListener("visibilitychange", vis);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", size);
      window.removeEventListener("pointerdown", down);
      window.removeEventListener("pointermove", move);
      document.removeEventListener("visibilitychange", vis);
    };
  }, []);
  return <canvas ref={ref} data-testid="water-ripple" aria-hidden className="pointer-events-none fixed inset-0 -z-10 h-full w-full" />;
};
